import { useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { errorCodeOf, type BodyFigure, type UnitsPreference } from "@heraion/shared";

import { BodyMap } from "../bodymap/BodyMap";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import {
  LANDMARK_ZONE_GLYPH,
  landmarkZoneFill,
  landmarkZoneWord,
} from "../primitives/LandmarkBar";
import { LoadingIndicator } from "../primitives/LoadingIndicator";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";
import { BuildTodayLink } from "./BuildTodayLink";
import { IncompleteNote } from "../primitives/IncompleteNote";
import { WidgetTitleRow } from "./InfoPill";
import { isOfflineCode } from "../loadErrorText";
import { LoadErrorLine, StaleHint } from "./panelCards";
import { landmarkZoneBodyMapValue, type MuscleCoverage } from "./muscleCoverage";
import {
  MuscleCoverageCompactRow,
  compactReading,
} from "./MuscleCoverageCompactRow";
import type { ProgressViewer } from "./types";
import type { SessionResultsStatus } from "./useSessionResults";

// PG-64 fetta 2: the muscle coverage, the one card on Progress, the Home
// widget and the coach's athlete detail (Antenore, 29/09: «ovunque, con le
// funzioni»). The critic's compact form, on the last 7 days against the
// whole band (#463, 26/10: no pace, no partial week); only the targeted
// muscles (#393) and those out of the band are open, the others wait behind
// «see all». What the old card did stays,
// in its old form: the three answers that are not a coverage (PG-8), the
// unconfirmed numbers (PG-15), «Build today's session» (AP-9b), the target
// and «Close the gap» on each row (MuscleCoverageCompactRow), and on top the
// figure painted by the band with its legend (Antenore, 29/09: it stays).

// Card-width floor for the two-column (figure | list) layout, measured on
// the CARD, not the window: figure column ~250 + gap + padding + a ~340 list.
const COVERAGE_TWO_COL_MIN = 640;
// The figure column's cap: the dual figure is ~240 wide, and without a cap
// the legend laid out on one line widened the column past the card (bench,
// 720 px, 29/09); capped, the legend wraps under the figure.
const FIGURE_COL_MAX = 280;

export type MuscleCoverageCompactSectionProps = {
  theme: UiTheme;
  /** Null when the read has not resolved, failed, or scored nothing: three
   *  DIFFERENT answers, told apart by `loading` / `error` below. */
  coverage: MuscleCoverage | null;
  units: UnitsPreference;
  locale: string;
  figure?: BodyFigure;
  isDark?: boolean;
  /** The session-results read has not come back yet. */
  loading?: boolean;
  /** The session-results read failed. */
  error?: unknown;
  /** «refreshing» and «error» WITH numbers on screen both mean these are the
   *  last numbers we hold, not ones the server just confirmed. */
  status?: SessionResultsStatus;
  /** The coach reads the athlete's target without «your». Default: the
   *  athlete's own Progress. */
  viewer?: ProgressViewer;
  /** AP-9b: the «Build today's session» link, athlete view only. */
  onBuildToday?: () => void;
};

export function MuscleCoverageCompactSection({
  theme,
  coverage,
  units,
  locale,
  figure,
  isDark = false,
  loading = false,
  error,
  status,
  viewer = "athlete",
  onBuildToday,
}: MuscleCoverageCompactSectionProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const errorCode = typeof error === "string" ? error : errorCodeOf(error);
  const { expanded, toggle, a11yProps } = useDisclosure(false);
  const [cardWidth, setCardWidth] = useState(0);
  const twoCol = cardWidth >= COVERAGE_TWO_COL_MIN;
  // The figure reads the BAND, like the legend under it and the rows (§16.4bis).
  const bodyValue = useMemo(
    () =>
      landmarkZoneBodyMapValue(
        (coverage?.rows ?? []).map((r) => r.landmark),
        theme,
      ),
    [coverage, theme],
  );
  // The model owns the order (#393: targeted first, then the band). This card
  // only folds: closed, it keeps the open rows, in that same order.
  const readings = useMemo(
    () => (coverage ? coverage.rows.map(compactReading) : []),
    [coverage],
  );

  const title = t("progress.coverageTitle", { defaultValue: "Muscle coverage" });
  const subtitle = t("progress.coverageCompact.subtitle", {
    defaultValue: "hard sets in the last 7 days vs the band",
  });
  // ONE eyebrow for every state below, with the heading role: this card is
  // the first thing on Progress and on the coach's athlete detail.
  const eyebrow = (
    <WidgetTitleRow
      theme={theme}
      title={
        <View
          style={styles.eyebrow}
          accessibilityRole="header"
          accessibilityLabel={`${title}. ${subtitle}`}
        >
          <Text style={styles.eyebrowLbl}>{title}</Text>
          <Text style={styles.eyebrowAux}>{subtitle}</Text>
        </View>
      }
    />
  );

  // PG-8: a broken read, a read in flight and an empty history look apart.
  if (!coverage) {
    return (
      <View style={styles.wrap}>
        {eyebrow}
        <View style={[styles.card, error && !isOfflineCode(errorCode) ? styles.cardError : null]}>
          {error ? (
            <LoadErrorLine theme={theme} error={errorCode} />
          ) : loading ? (
            <LoadingIndicator theme={theme} />
          ) : (
            <Text style={styles.stateText}>
              {t("progress.coverageNoData")}
            </Text>
          )}
        </View>
      </View>
    );
  }

  // #116: only catalogue-less exercises this week: no row to flag, so say it
  // here (word + (i) pill) rather than the "no data" of an empty history.
  if (coverage.rows.length === 0 && coverage.incomplete) {
    return (
      <View style={styles.wrap}>
        {eyebrow}
        <View style={styles.card}>
          <IncompleteNote theme={theme} reasons={coverage.incompleteReasons} />
        </View>
      </View>
    );
  }

  // PG-15: numbers the network has not confirmed. One string, shown and
  // spoken once.
  const unconfirmed = status === "refreshing" || status === "error";
  const openCount = readings.filter((r) => r.open).length;
  const shown = expanded ? readings : readings.filter((r) => r.open);
  const hiddenCount = readings.length - openCount;
  // The summary: only the counts that are not zero, above MRV first (a
  // muscle past MRV yesterday still shows today). Nothing to say, no line.
  const lead = [
    coverage.aboveMrvCount > 0
      ? t("progress.coverageCompact.leadAboveMrv", {
          count: coverage.aboveMrvCount,
          defaultValue: "Above the maximum recoverable volume: {{count}} muscles. Returns flatten here.",
        })
      : null,
    coverage.belowMevCount > 0
      ? t("progress.coverageCompact.leadBelowMev", {
          count: coverage.belowMevCount,
          defaultValue: "Below the minimum effective volume: {{count}} muscles.",
        })
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  // Four zones, the glyph in the label: two of them share the green.
  const legendItems = (["below-mev", "mev-mav", "mav-mrv", "above-mrv"] as const).map(
    (z) => ({
      color: landmarkZoneFill(theme, z),
      label: `${LANDMARK_ZONE_GLYPH[z]} ${landmarkZoneWord(t, z)}`,
    }),
  );

  return (
    <View style={styles.wrap}>
      {eyebrow}
      <View
        style={[styles.card, twoCol ? styles.cardWide : null]}
        onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}
      >
        {Object.keys(bodyValue).length > 0 ? (
          // Decorative for the ear: each row says its numbers and its zone word.
          <View
            style={[styles.figureBlock, twoCol ? styles.figureBlockWide : null]}
            {...hiddenFromScreenReader()}
          >
            <BodyMap
              mode="display"
              view="front"
              value={bodyValue}
              theme={theme}
              isDark={isDark}
              scale={1.1}
              figure={figure}
              layout="dual"
            />
            <ScaleLegend variant="dots" theme={theme} items={legendItems} />
          </View>
        ) : null}
        <View style={[styles.body, twoCol ? styles.bodyWide : null]}>
          {unconfirmed ? (
            <StaleHint theme={theme} refreshing={status === "refreshing"} />
          ) : null}
          {lead ? <Text style={styles.lead}>{lead}</Text> : null}
          {onBuildToday &&
          viewer === "athlete" &&
          coverage.mode !== "degraded" &&
          coverage.belowMevCount > 0 ? (
            <BuildTodayLink theme={theme} onPress={onBuildToday} />
          ) : null}
          <View>
            {shown.map((r, idx) => (
              <MuscleCoverageCompactRow
                key={r.entry.landmark.muscle}
                theme={theme}
                reading={r}
                units={units}
                locale={locale}
                divider={idx > 0}
                viewer={viewer}
              />
            ))}
          </View>
          {hiddenCount > 0 ? (
            <Pressable
              onPress={toggle}
              {...a11yProps}
              accessibilityRole="button"
              style={styles.seeAll}
            >
              <Text style={styles.seeAllTxt}>
                {expanded
                  ? t("progress.coverageShowFewer", { defaultValue: "Show fewer" })
                  : t("progress.coverageSeeAll", {
                      count: hiddenCount,
                      defaultValue: "See all muscles (+{{count}})",
                    })}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    eyebrow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.s3,
      paddingHorizontal: s.s1,
    },
    // ink3 at sm: the AA floor on every theme's bg (paletteContrast), and
    // the DS-16 12pt label floor.
    eyebrowLbl: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    eyebrowAux: { color: p.ink3, fontSize: ty.sizes.sm },
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    cardError: { borderColor: p.danger },
    // Wide: figure | list, the figure pinned top at its own width.
    cardWide: { flexDirection: "row", alignItems: "flex-start", gap: s.s5 },
    figureBlock: { alignItems: "center", gap: s.s2 },
    figureBlockWide: { flexShrink: 0, maxWidth: FIGURE_COL_MAX },
    body: { gap: s.s3 },
    bodyWide: { flex: 1, minWidth: 320 },
    stateText: { color: p.ink3, fontSize: ty.sizes.sm },
    lead: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "600",
      lineHeight: ty.sizes.base * 1.35,
    },
    seeAll: { paddingVertical: s.s2, minHeight: 44, justifyContent: "center" },
    seeAllTxt: { color: p.brandText, fontSize: ty.sizes.base, fontWeight: "600" },
  });
}
